import {
    IonAvatar,
    IonBadge,
    IonButton,
    IonButtons,
    IonContent,
    IonHeader,
    IonIcon,
    IonPage,
    IonProgressBar,
    IonRefresher,
    IonRefresherContent,
    IonTitle,
    IonToolbar,
    RefresherEventDetail
} from "@ionic/react";
import {
    arrowBackOutline,
    chevronForwardOutline,
    americanFootballOutline,
    refreshOutline,
    statsChartOutline
} from "ionicons/icons";
import {
    useCallback,
    useEffect,
    useMemo,
    useState
} from "react";
import { useHistory, useParams } from "react-router-dom";
import {
    Game,
    Player as PlayerModel,
    PlayerStatistics,
    Team,
    getGameDetail,
    getGames,
    getPlayerDetail,
    getPlayersList,
    getTeams
} from "../services/mexfbaApi";
import {
    PlayerGame,
    calculateStats,
    getAverage,
    getPassingRating,
    getPlays,
    playerAppearsInPlay
} from "./Player";
import "./PlayerStats.css";

interface RouteParams {
    id: string;
}

interface StatisticRow {
    label: string;
    value: string | number;
}

interface StatsBlock {
    title: string;
    rows: StatisticRow[];
}

function createEmptyStats(): PlayerStatistics {
    return {
        passingAttempts: 0,
        passingCompletions: 0,
        passingYards: 0,
        passingTouchdowns: 0,
        interceptionsThrown: 0,

        rushingAttempts: 0,
        rushingYards: 0,
        rushingTouchdowns: 0,

        receptions: 0,
        receivingYards: 0,
        receivingTouchdowns: 0,

        soloTackles: 0,
        assistedTackles: 0,
        sacks: 0,
        assistedSacks: 0,
        interceptions: 0,
        forcedFumbles: 0,
        recoveredFumbles: 0,
        defensiveTouchdowns: 0,
        passDeflections: 0,

        fieldGoalsMade: 0,
        fieldGoalsAttempted: 0,
        extraPointsMade: 0,
        extraPointsAttempted: 0,
        longestFieldGoal: 0,

        gamesPlayed: 0
    };
}

function getPlayerImageUrl(image?: string): string {
    if (!image) {
        return "";
    }

    if (
        image.startsWith("http://") ||
        image.startsWith("https://") ||
        image.startsWith("data:")
    ) {
        return image;
    }

    const normalizedImage = image.replace(/^(\.\.\/)+/, "");

    return `${process.env.PUBLIC_URL || ""}/${normalizedImage}`;
}

function getPositionShortName(position?: string): string {
    const positions: Record<string, string> = {
        "1": "QB",
        "2": "RB",
        "3": "WR",
        "4": "DL",
        "5": "LB",
        "6": "DB",
        "7": "K",
        "8": "OL"
    };

    return positions[position || ""] || "—";
}

function formatDate(date?: string): string {
    if (!date) {
        return "Fecha por confirmar";
    }

    const parsedDate = new Date(
        date.length === 10 ? `${date}T12:00:00` : date
    );

    if (Number.isNaN(parsedDate.getTime())) {
        return date;
    }

    return new Intl.DateTimeFormat("es-MX", {
        day: "numeric",
        month: "short",
        year: "numeric"
    }).format(parsedDate);
}

function getStatBlocks(
    player: PlayerModel,
    stats: PlayerStatistics
): StatsBlock[] {
    const position = player.posicion;

    const blocks: StatsBlock[] = [];

    if (position === "1") {
        blocks.push({
            title: "Pase",
            rows: [
                {
                    label: "Intentos",
                    value: stats.passingAttempts
                },
                {
                    label: "Completos",
                    value: stats.passingCompletions
                },
                {
                    label: "Porcentaje",
                    value: `${getAverage(
                        stats.passingCompletions * 100,
                        stats.passingAttempts
                    )}%`
                },
                {
                    label: "Yardas",
                    value: stats.passingYards
                },
                {
                    label: "Yardas por intento",
                    value: getAverage(
                        stats.passingYards,
                        stats.passingCompletions
                    )
                },
                {
                    label: "Touchdowns",
                    value: stats.passingTouchdowns
                },
                {
                    label: "Intercepciones",
                    value: stats.interceptionsThrown
                },
                {
                    label: "QBR",
                    value: getPassingRating(stats)
                }
            ]
        });
    }

    if (position === "2" || position === "3") {
        blocks.push({
            title: position === "2" ? "Carrera" : "Producción terrestre",
            rows: [
                {
                    label: "Acarreos",
                    value: stats.rushingAttempts
                },
                {
                    label: "Yardas",
                    value: stats.rushingYards
                },
                {
                    label: "Promedio",
                    value: getAverage(
                        stats.rushingYards,
                        stats.rushingAttempts
                    )
                },
                {
                    label: "Touchdowns",
                    value: stats.rushingTouchdowns
                }
            ]
        });
    }

    if (position === "2" || position === "3") {
        blocks.push({
            title:
                position === "3"
                    ? "Recepciones"
                    : "Producción aérea",
            rows: [
                {
                    label: "Recepciones",
                    value: stats.receptions
                },
                {
                    label: "Yardas",
                    value: stats.receivingYards
                },
                {
                    label: "Promedio",
                    value: getAverage(
                        stats.receivingYards,
                        stats.receptions
                    )
                },
                {
                    label: "Touchdowns",
                    value: stats.receivingTouchdowns
                }
            ]
        });
    }

    if (
        position === "4" ||
        position === "5" ||
        position === "6"
    ) {
        blocks.push({
            title: "Defensiva",
            rows: [
                {
                    label: "Tacleadas solitarias",
                    value: stats.soloTackles
                },
                {
                    label: "Tacleadas asistidas",
                    value: stats.assistedTackles
                },
                {
                    label: "Tacleadas totales",
                    value:
                        stats.soloTackles +
                        stats.assistedTackles
                },
                {
                    label: "Sacks",
                    value: (
                        stats.sacks +
                        stats.assistedSacks / 2
                    ).toFixed(1)
                },
                {
                    label: "Intercepciones",
                    value: stats.interceptions
                },
                {
                    label: "Fumbles forzados",
                    value: stats.forcedFumbles
                },
                {
                    label: "Fumbles recuperados",
                    value: stats.recoveredFumbles
                },
                {
                    label: "Pases defendidos",
                    value: stats.passDeflections
                },
                {
                    label: "Touchdowns defensivos",
                    value: stats.defensiveTouchdowns
                }
            ]
        });
    }

    if (position === "7") {
        blocks.push({
            title: "Patadas",
            rows: [
                {
                    label: "Goles de campo",
                    value: `${stats.fieldGoalsMade}/${stats.fieldGoalsAttempted}`
                },
                {
                    label: "Puntos extra",
                    value: `${stats.extraPointsMade}/${stats.extraPointsAttempted}`
                },
                {
                    label: "Gol de campo más largo",
                    value: stats.longestFieldGoal
                }
            ]
        });
    }

    if (blocks.length === 0) {
        blocks.push({
            title: "Actividad",
            rows: [
                {
                    label: "Partidos jugados",
                    value: stats.gamesPlayed
                },
                {
                    label: "Yardas terrestres",
                    value: stats.rushingYards
                },
                {
                    label: "Yardas por recepción",
                    value: stats.receivingYards
                },
                {
                    label: "Touchdowns",
                    value:
                        stats.rushingTouchdowns +
                        stats.receivingTouchdowns +
                        stats.defensiveTouchdowns
                }
            ]
        });
    }

    return blocks;
}

function getGameSummary(
    player: PlayerModel,
    stats: PlayerStatistics
): string {
    if (player.posicion === "1") {
        return `${stats.passingCompletions}/${stats.passingAttempts} CMP · ${stats.passingYards} YDS · ${stats.passingTouchdowns} TD`;
    }

    if (player.posicion === "2") {
        return `${stats.rushingAttempts} ATT · ${stats.rushingYards} YDS · ${stats.rushingTouchdowns} TD`;
    }

    if (player.posicion === "3") {
        return `${stats.receptions} REC · ${stats.receivingYards} YDS · ${stats.receivingTouchdowns} TD`;
    }

    if (
        player.posicion === "4" ||
        player.posicion === "5" ||
        player.posicion === "6"
    ) {
        return `${stats.soloTackles + stats.assistedTackles} TKL · ${(
            stats.sacks +
            stats.assistedSacks / 2
        ).toFixed(1)} SACK · ${stats.interceptions} INT`;
    }

    if (player.posicion === "7") {
        return `${stats.fieldGoalsMade}/${stats.fieldGoalsAttempted} FG · ${stats.extraPointsMade}/${stats.extraPointsAttempted} XP`;
    }

    return `${stats.gamesPlayed} PJ`;
}

const PlayerStats: React.FC = () => {
    const { id } = useParams<RouteParams>();
    const history = useHistory();

    const [player, setPlayer] = useState<PlayerModel | null>(
        null
    );

    const [team, setTeam] = useState<Team | null>(null);
    const [regularStats, setRegularStats] =
        useState<PlayerStatistics>(createEmptyStats());

    const [playoffStats, setPlayoffStats] =
        useState<PlayerStatistics>(createEmptyStats());

    const [playerGames, setPlayerGames] = useState<PlayerGame[]>(
        []
    );

    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState("");

    const loadPlayerStats = useCallback(
        async function (forceRefresh = false) {
            try {
                setError("");

                if (forceRefresh) {
                    setRefreshing(true);
                } else {
                    setLoading(true);
                }

                const [players, teams, games] = await Promise.all([
                    getPlayersList(forceRefresh),
                    getTeams(forceRefresh),
                    getGames(forceRefresh)
                ]);

                const basePlayer = players.find(function (currentPlayer) {
                    return currentPlayer.id === id;
                });

                if (!basePlayer) {
                    throw new Error("Jugador no encontrado.");
                }

                const detail = await getPlayerDetail(id, forceRefresh);

                const detailPlayer = detail.Info as PlayerModel & {
                    birthday?: string;
                };

                const selectedPlayer: PlayerModel = {
                    ...basePlayer,
                    ...detailPlayer,
                    nacimiento:
                        detailPlayer.nacimiento ||
                        detailPlayer.birthday ||
                        basePlayer.nacimiento,
                    img:
                        getPlayerImageUrl(detailPlayer.img) ||
                        getPlayerImageUrl(basePlayer.img) ||
                        undefined
                };

                const competitionGames = games.filter(function (game) {
                    return (
                        !selectedPlayer.comp_id ||
                        game.id_comp === selectedPlayer.comp_id
                    );
                });

                const gameDetails = await Promise.all(
                    competitionGames.map(async function (game) {
                        try {
                            const detail = await getGameDetail(
                                game.id,
                                forceRefresh
                            );

                            return { game, detail };
                        } catch {
                            return null;
                        }
                    })
                );

                const gamesWithPlayer = gameDetails
                    .filter(function (
                        item
                    ): item is PlayerGame {
                        return item !== null;
                    })
                    .filter(function (item) {
                        return getPlays(item.detail).some(function (play) {
                            return playerAppearsInPlay(
                                play,
                                selectedPlayer.id
                            );
                        });
                    });

                const regularGames = gamesWithPlayer.filter(function (
                    item
                ) {
                    return (
                        item.game.partido === "grupos" ||
                        !item.game.partido
                    );
                });

                const playoffGames = gamesWithPlayer.filter(function (
                    item
                ) {
                    return (
                        item.game.partido !== "grupos" &&
                        Boolean(item.game.partido)
                    );
                });

                setPlayer(selectedPlayer);

                setTeam(
                    teams.find(function (currentTeam) {
                        return currentTeam.id === selectedPlayer.equipo_id;
                    }) || null
                );

                setRegularStats(
                    calculateStats(regularGames, selectedPlayer)
                );

                setPlayoffStats(
                    calculateStats(playoffGames, selectedPlayer)
                );

                setPlayerGames(
                    [...gamesWithPlayer].sort(function (first, second) {
                        return (
                            new Date(
                                second.game.fecha || 0
                            ).getTime() -
                            new Date(
                                first.game.fecha || 0
                            ).getTime()
                        );
                    })
                );
            } catch (requestError) {
                console.error(
                    "No fue posible cargar estadísticas del jugador:",
                    requestError
                );

                setError(
                    requestError instanceof Error
                        ? requestError.message
                        : "No fue posible cargar las estadísticas."
                );
            } finally {
                setLoading(false);
                setRefreshing(false);
            }
        },
        [id]
    );

    useEffect(
        function () {
            loadPlayerStats();
        },
        [loadPlayerStats]
    );

    const regularBlocks = useMemo(
        function () {
            return player
                ? getStatBlocks(player, regularStats)
                : [];
        },
        [player, regularStats]
    );

    const playoffBlocks = useMemo(
        function () {
            return player
                ? getStatBlocks(player, playoffStats)
                : [];
        },
        [player, playoffStats]
    );

    async function handleRefresh(
        event: CustomEvent<RefresherEventDetail>
    ) {
        await loadPlayerStats(true);
        event.detail.complete();
    }

    if (loading) {
        return (
            <IonPage>
                <IonHeader>
                    <IonToolbar>
                        <IonTitle>Estadísticas</IonTitle>
                    </IonToolbar>
                </IonHeader>

                <IonContent>
                    <IonProgressBar type="indeterminate" />
                </IonContent>
            </IonPage>
        );
    }

    if (error || !player) {
        return (
            <IonPage>
                <IonHeader>
                    <IonToolbar>
                        <IonButtons slot="start">
                            <IonButton
                                aria-label="Regresar"
                                onClick={function () {
                                    history.goBack();
                                }}
                            >
                                <IonIcon icon={arrowBackOutline} />
                            </IonButton>
                        </IonButtons>

                        <IonTitle>Estadísticas</IonTitle>
                    </IonToolbar>
                </IonHeader>

                <IonContent className="player-stats-content">
                    <div className="player-stats-empty">
                        <IonIcon icon={statsChartOutline} />
                        <strong>
                            No fue posible cargar las estadísticas
                        </strong>
                        <span>{error}</span>

                        <IonButton
                            onClick={function () {
                                loadPlayerStats(true);
                            }}
                        >
                            Reintentar
                        </IonButton>
                    </div>
                </IonContent>
            </IonPage>
        );
    }

    return (
        <IonPage>
            <IonHeader translucent>
                <IonToolbar className="player-stats-toolbar">
                    <IonButtons slot="start">
                        <IonButton
                            aria-label="Regresar al jugador"
                            onClick={function () {
                                history.push(`/player/${player.id}`);
                            }}
                        >
                            <IonIcon icon={arrowBackOutline} />
                        </IonButton>
                    </IonButtons>

                    <IonTitle>Estadísticas</IonTitle>

                    <IonButtons slot="end">
                        <IonButton
                            aria-label="Actualizar estadísticas"
                            disabled={refreshing}
                            onClick={function () {
                                loadPlayerStats(true);
                            }}
                        >
                            <IonIcon icon={refreshOutline} />
                        </IonButton>
                    </IonButtons>
                </IonToolbar>
            </IonHeader>

            <IonContent fullscreen className="player-stats-content">
                <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
                    <IonRefresherContent
                        pullingText="Desliza para actualizar"
                        refreshingText="Actualizando estadísticas..."
                    />
                </IonRefresher>

                <section
                    className="player-stats-hero"
                    style={{
                        background: `linear-gradient(135deg, ${
                            team?.color || "#102b4d"
                        }, #173e70)`
                    }}
                >
                    <IonAvatar>
                        {player.img ? (
                            <img
                                src={player.img}
                                alt={`Foto de ${player.nombre}`}
                            />
                        ) : (
                            <span>{player.nombre.charAt(0)}</span>
                        )}
                    </IonAvatar>

                    <div>
                        <span>
                            {getPositionShortName(player.posicion)} · #
                            {player.numero || player.num || "—"}
                        </span>
                        <h1>{player.nombre}</h1>
                        <p>{team?.nombre || "Equipo no disponible"}</p>

                    </div>
                </section>

                <nav className="player-stats-navigation">
                    <IonButton
                        fill="outline"
                        onClick={function () {
                            history.push(`/player/${player.id}`);
                        }}
                    >
                        Ficha
                    </IonButton>

                    <IonButton>Estadísticas</IonButton>
                </nav>

                <main className="player-stats-page">
                    <section className="player-stats-summary">
                        <div>
                            <span>ESTADÍSTICAS ACUMULADAS</span>
                            <h2>Temporada regular</h2>
                        </div>

                        <IonBadge>{regularStats.gamesPlayed} PJ</IonBadge>
                    </section>

                    {regularBlocks.map(function (block) {
                        return (
                            <section
                                className="player-stats-card"
                                key={`regular-${block.title}`}
                            >
                                <h3>{block.title}</h3>

                                <div className="player-stats-card__grid">
                                    {block.rows.map(function (row) {
                                        return (
                                            <div key={row.label}>
                                                <strong>{row.value}</strong>
                                                <span>{row.label}</span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </section>
                        );
                    })}

                    <section className="player-stats-summary">
                        <div>
                            <span>POSTEMPORADA</span>
                            <h2>Playoffs</h2>
                        </div>

                        <IonBadge color="warning">
                            {playoffStats.gamesPlayed} PJ
                        </IonBadge>
                    </section>

                    {playoffBlocks.map(function (block) {
                        return (
                            <section
                                className="player-stats-card"
                                key={`playoff-${block.title}`}
                            >
                                <h3>{block.title}</h3>

                                <div className="player-stats-card__grid">
                                    {block.rows.map(function (row) {
                                        return (
                                            <div key={row.label}>
                                                <strong>{row.value}</strong>
                                                <span>{row.label}</span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </section>
                        );
                    })}

                    <section className="player-stats-summary">
                        <div>
                            <span>DESGLOSE COMPLETO</span>
                            <h2>Partido por partido</h2>
                        </div>

                        <IonIcon icon={statsChartOutline} />
                    </section>

                    {playerGames.length === 0 ? (
                        <div className="player-stats-no-games">
                            <IonIcon icon={americanFootballOutline} />
                            <strong>Sin actividad registrada</strong>
                            <span>
                                Aún no existen jugadas asociadas a este
                                jugador.
                            </span>
                        </div>
                    ) : (
                        <div className="player-stats-games">
                            {playerGames.map(function (playerGame) {
                                const game = playerGame.game;

                                const stats = calculateStats(
                                    [playerGame],
                                    player
                                );

                                const isLocal =
                                    game.id_local === player.equipo_id;

                                const rival = isLocal
                                    ? game.nombre_visita
                                    : game.nombre_local;

                                return (
                                    <button
                                        className="player-stats-game"
                                        key={game.id}
                                        type="button"
                                        onClick={function () {
                                            history.push(
                                                `/game/${game.id}`
                                            );
                                        }}
                                    >
                                        <div className="player-stats-game__top">
                                            <span>
                                                {isLocal ? "VS" : "EN"}
                                            </span>

                                            <strong>
                                                {rival || "Rival"}
                                            </strong>

                                            <small>
                                                {formatDate(game.fecha)}
                                            </small>
                                        </div>

                                        <p>
                                            {getGameSummary(player, stats)}
                                        </p>

                                        <IonIcon
                                            icon={chevronForwardOutline}
                                        />
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </main>
            </IonContent>
        </IonPage>
    );
};

export default PlayerStats;
